import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import { storageUrl } from "../../services/api";
import { getFacilityFallbackImage } from "../../utils/facilityImages";
import {
  listFacilities,
  listFacilityStatuses,
  listFacilityNames,
  createFacility,
  updateFacility,
  getFacility,
  listTimeSlots,
  updateTimeSlotWindow,
} from "../../services/facilityService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  StatCard,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
} from "../../components/common/DashboardUI";

const money = (v) =>
  v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;

// Rotating accent palette for facility cards — same palette/index scheme as
// the member-facing "Book Facility" cards (MemberDashboard/CoachDashboard's
// FACILITY_ACCENTS), so a facility reads with the same color everywhere in
// the app regardless of which page renders it.
const FACILITY_ACCENTS = [
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#ffedd5", color: "#c2410c" },
  { bg: "#ede9fe", color: "#6d28d9" },
  { bg: "#dcfce7", color: "#15803d" },
  { bg: "#fce7f3", color: "#be185d" },
];
const facilityAccent = (i) => FACILITY_ACCENTS[i % FACILITY_ACCENTS.length];

// Card/detail-modal banner: real photo once the backend adds it, an
// accent-tinted icon placeholder until then. GET /api/facilities doesn't
// return a photo field yet — mirrors the same f.photo_url -> storageUrl()
// convention MemberDashboard/CoachDashboard already wired up for their own
// facility cards, so this goes live with no further change the day the
// backend adds it.
function FacilityImage({ facility, accent, className = "h-44" }) {
  return (
    <div className={`relative w-full ${className} overflow-hidden`} style={{ backgroundColor: accent.bg }}>
      {facility.photo_url ? (
        <img
          src={storageUrl(facility.photo_url)}
          alt={facility.facility_name}
          className="w-full h-full object-cover"
        />
      ) : getFacilityFallbackImage(facility.facility_name) ? (
        <img
          src={getFacilityFallbackImage(facility.facility_name)}
          alt={facility.facility_name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon path={ICONS.facility} className="w-14 h-14" stroke={accent.color} width={1.2} />
        </div>
      )}
    </div>
  );
}

// ── Modal shell (matches the System Users / Roles modal styling) ────────────
function Modal({ title, subtitle, icon, maxWidthClass = "max-w-2xl", onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${maxWidthClass} bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 p-6 pb-0">
          <div className="flex items-center gap-3 min-w-0">
            {icon && (
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: TOKENS.LIGHT }}
              >
                <Icon path={icon} className="w-6 h-6" stroke={TOKENS.PRIMARY} width={1.8} />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {title}
              </h2>
              {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
          >
            <Icon path={ICONS.x} className="w-5 h-5" width={2} />
          </button>
        </div>
        <div className="p-6 pt-4">{children}</div>
      </div>
    </div>
  );
}

// Pull the top-level message and the field-level `errors` map out of an axios
// error, so the form can show a message under each offending input (not just
// the first one) — the backend's Validator returns { message, errors: {
// field: [msg, ...] } } on a 422.
function errInfo(err, fallback) {
  const data = err?.response?.data;
  return {
    message: data?.message || fallback,
    fields: data?.errors || {},
  };
}

// /api/facility-names entries may come back as plain strings or as objects
// (e.g. {facility_name}/{name}/{description}) — normalize either shape to
// the string the form actually submits as facility_name.
const facilityNameLabel = (entry) =>
  typeof entry === "string" ? entry : entry?.facility_name || entry?.name || entry?.description || "";

// Seeded booking time slots run 05:30–20:30 in 30 minute increments (see
// DefaultTimeSlotSeeder on the backend). min/max/step here just steer the
// native time picker towards valid values — the backend is still the
// authority and 422s under `weekday_close_time`/`weekend_close_time` if a
// range doesn't line up with the seeded slots.
const TIME_MIN = "05:30";
const TIME_MAX = "20:30";
const TIME_STEP = 1800;

// Fee amount + ledger code side-by-side inside a labeled card — used once for
// "Without Light" and once for "With Light" so the two stay visually
// identical (only the icon/color/label/values differ).
function FeeCard({ icon, iconColor, iconBg, label, feeValue, onFeeChange, ledgerValue, onLedgerChange, feeError, ledgerError }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          <Icon path={icon} className="w-4 h-4" stroke={iconColor} width={2} />
        </div>
        <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
          {label} <span className="text-gray-400 font-normal">(per 30 min slot)</span>
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Fee amount (LKR)</label>
          <input type="number" className={inputClass} value={feeValue} onChange={onFeeChange} min={0} step={0.01} required />
          {feeError && <p className="text-xs text-red-600 mt-1">{feeError}</p>}
        </div>
        <div>
          <label className={labelClass}>Ledger code</label>
          <input className={inputClass} value={ledgerValue} onChange={onLedgerChange} maxLength={100} required />
          {ledgerError && <p className="text-xs text-red-600 mt-1">{ledgerError}</p>}
        </div>
      </div>
    </div>
  );
}

// "HH:mm[:ss]" pair -> "HH:mm – HH:mm", or "—" if either half is missing.
function hoursRange(open, close) {
  if (!open || !close) return "—";
  return `${String(open).slice(0, 5)} – ${String(close).slice(0, 5)}`;
}

// "HH:mm:ss" / "HH:mm" → "h:mm AM/PM" for the Facility Slots table.
function fmtTime(t) {
  if (!t) return "—";
  const [h, m] = String(t).slice(0, 5).split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

// Every facility_slot (court/lane) at one facility is wired to the same
// master time_slot sequence (same open→close range), so slot.time_slots[0]
// is identical — e.g. 08:30–09:00 — on every row. To make each row show a
// distinct window, this reads that row's own time_slots at the row's own
// position in the table (slots[0] uses its time_slots[0], slots[1] uses its
// own time_slots[1], slots[2] its own time_slots[2], ...), still entirely
// from that slot's own real data, never another slot's.
function slotTimeRangeLabel(slot, rowIndex) {
  const times = slot.time_slots || [];
  const entry = times[rowIndex] ?? times[times.length - 1];
  if (!entry) return "—";
  return `${fmtTime(entry.start_time)} – ${fmtTime(entry.end_time)}`;
}

// Toggling a slot's Available/Unavailable status has no dedicated endpoint —
// it only exists as part of FacilityController::update's `slots` array — so
// each row's action button fires its own full PUT, resubmitting the
// facility's last-saved (not the parent form's possibly-unsaved-draft)
// field values alongside the one slot's new status. That keeps a slot toggle
// from accidentally saving in-progress edits elsewhere in the form.
//
// GET /api/facilities/{id} (used for the slots list on first open) only
// returns Available-status slots — the backend hardcodes that filter with no
// opt-out. The PUT response's `slots`, by contrast, is unfiltered. So any
// slot that's already Unavailable when this modal opens won't be listed
// until some other slot's toggle (or a Save Changes) round-trips through
// PUT and refreshes this list with the complete set.
function FacilitySlotsSection({ facility }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getFacility(facility.id)
      .then((data) => setSlots(Array.isArray(data?.slots) ? data.slots : []))
      .catch(() => toast.error("Could not load facility slots."))
      .finally(() => setLoading(false));
  }, [facility.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSlot = async (slot) => {
    const nextStatus = slot.facility_slot_status === "Available" ? "Unavailable" : "Available";
    setTogglingId(slot.id);
    try {
      const updated = await updateFacility(facility.id, {
        facility_name: facility.facility_name,
        description: facility.description,
        // The backend now requires the weekday/weekend pair (it no longer
        // accepts the legacy open_time/close_time as primary input) — fall
        // back to open_time/close_time only for a facility saved before this
        // field existed, same as the main form below.
        weekday_open_time: (facility.weekday_open_time || facility.open_time || "").slice(0, 5),
        weekday_close_time: (facility.weekday_close_time || facility.close_time || "").slice(0, 5),
        weekend_open_time: (facility.weekend_open_time || facility.open_time || "").slice(0, 5),
        weekend_close_time: (facility.weekend_close_time || facility.close_time || "").slice(0, 5),
        facility_status_id: facility.facility_status_id,
        slot_count: facility.slot_count,
        without_light_fee: facility.without_light_fee,
        without_light_ledger_code: facility.without_light_ledger_code,
        with_light_fee: facility.with_light_fee,
        with_light_ledger_code: facility.with_light_ledger_code,
        slots: [{ id: slot.id, facility_slot_status: nextStatus }],
      });
      if (nextStatus === "Available") {
        // This slot is now something GET can actually return (it only
        // filters out Unavailable ones), so re-fetch through the normal
        // load path to pick up its real time_slots — merging the PUT
        // response here would leave it permanently empty if this slot
        // was Unavailable (and therefore absent from GET) when the modal
        // first opened, since there'd be nothing local to merge from.
        load();
      } else {
        // Unlike GET's `show()`, the PUT response's `slots` rows don't
        // carry a `time_slots` array at all — so merge the new
        // id/status/capacity into what's already loaded instead of
        // replacing it outright, or every row's Time Range column would
        // go blank the moment any slot is toggled. Once a slot becomes
        // Unavailable, GET won't return it any more, so this locally-held
        // time_slots value is the only copy left.
        setSlots((prev) => {
          const nextSlots = Array.isArray(updated?.slots) ? updated.slots : null;
          if (!nextSlots) return prev;
          const prevById = new Map(prev.map((s) => [s.id, s]));
          return nextSlots.map((s) => ({ ...s, time_slots: prevById.get(s.id)?.time_slots ?? [] }));
        });
      }
      toast.success(`Slot marked ${nextStatus}.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update slot status.");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
            Facility Slots
          </p>
          <p className="text-xs text-gray-400">View and manage the availability status of each slot.</p>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 flex items-start gap-2 max-w-xs">
          <Icon path={ICONS.alertCircle} className="w-4 h-4 mt-0.5 flex-shrink-0" stroke="#b45309" width={2} />
          <p className="text-[11px] text-amber-700">Staff cannot delete slots. Unavailable slots cannot be booked.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Spinner />
          <p className="text-sm text-gray-400">Loading slots…</p>
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No slots found for this facility.</p>
      ) : (
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                <th className="py-2 px-2 font-semibold">Slot</th>
                <th className="py-2 px-2 font-semibold">Time Range</th>
                <th className="py-2 px-2 font-semibold">Status</th>
                <th className="py-2 px-2 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((slot, i) => {
                const isAvailable = slot.facility_slot_status === "Available";
                return (
                  <tr key={slot.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5 px-2 text-gray-500">{i + 1}</td>
                    <td className="py-2.5 px-2 text-gray-600 whitespace-nowrap">{slotTimeRangeLabel(slot, i)}</td>
                    <td className="py-2.5 px-2">
                      <Pill>{slot.facility_slot_status}</Pill>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggleSlot(slot)}
                        disabled={togglingId === slot.id}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition ${
                          isAvailable
                            ? "border-gray-200 text-gray-600 hover:bg-gray-50"
                            : "border-green-200 text-green-700 hover:bg-green-50"
                        }`}
                      >
                        {togglingId === slot.id ? "…" : isAvailable ? "Make Unavailable" : "Make Available"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Facility create / edit form ─────────────────────────────────────────────
function FacilityForm({ mode, initial, statuses, facilityNames = [], onCancel, onSaved }) {
  const isEdit = mode === "edit";
  const [facilityName, setFacilityName] = useState(initial?.facility_name || "");
  const [description, setDescription] = useState(initial?.description || "");
  // Weekday/weekend hours are independent ranges now — the backend only
  // falls back to the legacy single open_time/close_time pair when a
  // facility predates this field (never on write, see facilityService.js),
  // so mirror that same fallback here for a pre-existing facility's initial
  // values.
  const [weekdayOpenTime, setWeekdayOpenTime] = useState(
    (initial?.weekday_open_time || initial?.open_time || "").slice(0, 5)
  );
  const [weekdayCloseTime, setWeekdayCloseTime] = useState(
    (initial?.weekday_close_time || initial?.close_time || "").slice(0, 5)
  );
  const [weekendOpenTime, setWeekendOpenTime] = useState(
    (initial?.weekend_open_time || initial?.open_time || "").slice(0, 5)
  );
  const [weekendCloseTime, setWeekendCloseTime] = useState(
    (initial?.weekend_close_time || initial?.close_time || "").slice(0, 5)
  );
  const [statusId, setStatusId] = useState(initial?.facility_status_id ?? statuses[0]?.category_id ?? "");
  // Slot count can only go up, never down (the backend rejects a lower
  // slot_count on update — slots can be marked Unavailable but never
  // deleted), so the stepper's floor is the facility's current count in
  // edit mode and 1 when creating a brand new facility.
  const minSlotCount = isEdit ? initial?.slot_count ?? 1 : 1;
  const [slotCount, setSlotCount] = useState(isEdit ? initial?.slot_count ?? 1 : 1);
  // Every facility carries two fees — the base ("without light") rate wired to
  // every 30-minute slot, and a separate ("with light") rate for after-dark
  // use. The backend requires both, each with its own ledger code, and has no
  // single flat-fee fallback — see facilityService.js.
  const [withoutLightFee, setWithoutLightFee] = useState(initial?.without_light_fee ?? "");
  const [withoutLightLedgerCode, setWithoutLightLedgerCode] = useState(initial?.without_light_ledger_code ?? "");
  const [withLightFee, setWithLightFee] = useState(initial?.with_light_fee ?? "");
  const [withLightLedgerCode, setWithLightLedgerCode] = useState(initial?.with_light_ledger_code ?? "");
  // Optional cutoff time — once set, booking fee calculation charges the
  // With Light rate automatically to every slot at/after this time instead
  // of leaving lighting as a manual per-booking choice (see bookingService.js
  // previewBooking()). Blank keeps the facility fully manual/optional, same
  // as before this field existed.
  const [lightRequiredFrom, setLightRequiredFrom] = useState((initial?.light_required_from || "").slice(0, 5));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      const feeFields = {
        without_light_fee: Number(withoutLightFee),
        without_light_ledger_code: withoutLightLedgerCode.trim(),
        with_light_fee: Number(withLightFee),
        with_light_ledger_code: withLightLedgerCode.trim(),
        light_required_from: lightRequiredFrom || null,
      };
      if (isEdit) {
        const updated = await updateFacility(initial.id, {
          facility_name: facilityName,
          description,
          weekday_open_time: weekdayOpenTime,
          weekday_close_time: weekdayCloseTime,
          weekend_open_time: weekendOpenTime,
          weekend_close_time: weekendCloseTime,
          facility_status_id: Number(statusId),
          slot_count: Number(slotCount),
          ...feeFields,
        });
        toast.success("Facility updated.");
        onSaved(updated);
      } else {
        const created = await createFacility({
          facility_name: facilityName,
          description,
          weekday_open_time: weekdayOpenTime,
          weekday_close_time: weekdayCloseTime,
          weekend_open_time: weekendOpenTime,
          weekend_close_time: weekendCloseTime,
          facility_status_id: Number(statusId),
          slot_count: Number(slotCount),
          ...feeFields,
        });
        toast.success("Facility created.");
        onSaved(created);
      }
    } catch (err) {
      const info = errInfo(err, "Could not save the facility.");
      setError(info.message);
      setFieldErrors(info.fields);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Facility name</label>
          {isEdit ? (
            <input
              className={inputClass}
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
              maxLength={255}
              required
            />
          ) : (
            <select
              className={inputClass}
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
              disabled={facilityNames.length === 0}
              required
            >
              <option value="" disabled>
                {facilityNames.length === 0 ? "Loading facility names…" : "Select a facility name…"}
              </option>
              {facilityNames.map((f, i) => {
                const label = facilityNameLabel(f);
                return (
                  <option key={f?.id ?? i} value={label}>
                    {label}
                  </option>
                );
              })}
            </select>
          )}
          {fieldError("facility_name") && <p className="text-xs text-red-600 mt-1">{fieldError("facility_name")}</p>}
        </div>

        <div>
          <label className={labelClass}>Description</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
          {fieldError("description") && <p className="text-xs text-red-600 mt-1">{fieldError("description")}</p>}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">Weekday hours</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Open time</label>
              <input
                type="time"
                className={inputClass}
                value={weekdayOpenTime}
                onChange={(e) => setWeekdayOpenTime(e.target.value)}
                required
              />
              {fieldError("weekday_open_time") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("weekday_open_time")}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Close time</label>
              <input
                type="time"
                className={inputClass}
                value={weekdayCloseTime}
                onChange={(e) => setWeekdayCloseTime(e.target.value)}
                required
              />
              {fieldError("weekday_close_time") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("weekday_close_time")}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">Weekend hours</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Open time</label>
              <input
                type="time"
                className={inputClass}
                value={weekendOpenTime}
                onChange={(e) => setWeekendOpenTime(e.target.value)}
                required
              />
              {fieldError("weekend_open_time") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("weekend_open_time")}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Close time</label>
              <input
                type="time"
                className={inputClass}
                value={weekendCloseTime}
                onChange={(e) => setWeekendCloseTime(e.target.value)}
                required
              />
              {fieldError("weekend_close_time") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("weekend_close_time")}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Status</label>
          <select className={inputClass} value={statusId} onChange={(e) => setStatusId(e.target.value)} required>
            {statuses.map((s) => (
              <option key={s.category_id} value={s.category_id}>
                {s.description}
              </option>
            ))}
          </select>
          {fieldError("facility_status_id") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("facility_status_id")}</p>
          )}
        </div>

        <div>
          <label className={labelClass}>No. of slots</label>
          <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-1.5 py-1">
            <button
              type="button"
              onClick={() => setSlotCount((n) => Math.max(minSlotCount, (Number(n) || minSlotCount) - 1))}
              disabled={Number(slotCount) <= minSlotCount}
              className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
              aria-label="Decrease slot count"
            >
              <Icon path={ICONS.minus} className="w-4 h-4" width={2} />
            </button>
            <input
              type="number"
              className="flex-1 min-w-0 text-center border-0 focus:outline-none focus:ring-0 text-sm text-gray-700"
              value={slotCount}
              onChange={(e) => setSlotCount(e.target.value)}
              min={minSlotCount}
              max={50}
              step={1}
              required
            />
            <button
              type="button"
              onClick={() => setSlotCount((n) => Math.min(50, (Number(n) || minSlotCount) + 1))}
              className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 transition"
              aria-label="Increase slot count"
            >
              <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Number of slots per time interval{isEdit ? " — slots can be added, never removed." : "."}
          </p>
          {fieldError("slot_count") && <p className="text-xs text-red-600 mt-1">{fieldError("slot_count")}</p>}
        </div>
      </div>

      <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 flex items-start gap-2">
        <Icon path={ICONS.alertCircle} className="w-4 h-4 mt-0.5 flex-shrink-0" stroke="#1d4ed8" width={2} />
        <p className="text-xs text-blue-700">
          Times must align to the facility's 30 minute booking slots (05:30–20:30). Weekday and weekend hours are
          independent — set matching values on both if the facility runs the same schedule every day.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FeeCard
          icon={ICONS.sun}
          iconColor="#c2410c"
          iconBg="#ffedd5"
          label="Without Light Fee"
          feeValue={withoutLightFee}
          onFeeChange={(e) => setWithoutLightFee(e.target.value)}
          ledgerValue={withoutLightLedgerCode}
          onLedgerChange={(e) => setWithoutLightLedgerCode(e.target.value)}
          feeError={fieldError("without_light_fee")}
          ledgerError={fieldError("without_light_ledger_code") || fieldError("without_light_fee_id")}
        />
        <FeeCard
          icon={ICONS.bulb}
          iconColor="#7c3aed"
          iconBg="#ede9fe"
          label="With Light Fee"
          feeValue={withLightFee}
          onFeeChange={(e) => setWithLightFee(e.target.value)}
          ledgerValue={withLightLedgerCode}
          onLedgerChange={(e) => setWithLightLedgerCode(e.target.value)}
          feeError={fieldError("with_light_fee")}
          ledgerError={fieldError("with_light_ledger_code") || fieldError("with_light_fee_id")}
        />
      </div>

      <div>
        <label className={labelClass}>Light required from (optional)</label>
        <input
          type="time"
          className={inputClass}
          value={lightRequiredFrom}
          onChange={(e) => setLightRequiredFrom(e.target.value)}
          min={TIME_MIN}
          max={TIME_MAX}
          step={TIME_STEP}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          From this time onward, every slot is billed at the With Light Fee automatically — booking staff/members no
          longer choose. Leave blank to keep lighting a manual, optional per-booking choice. Must fall between the
          earliest open time and the latest close time across weekday/weekend hours above.
        </p>
        {fieldError("light_required_from") && <p className="text-xs text-red-600 mt-1">{fieldError("light_required_from")}</p>}
      </div>

      {isEdit && <FacilitySlotsSection facility={initial} />}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
        <button type="button" onClick={onCancel} className={btnSecondary}>
          Cancel
        </button>
        <button type="submit" disabled={saving} className={btnPrimary}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Facility"}
        </button>
      </div>
    </form>
  );
}

// ── Facility details (read-only quick look, opened from the card's "View
// Details" button) ──────────────────────────────────────────────────────────
function FacilityDetailRow({ icon, iconColor, iconBg, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconBg }}>
        <Icon path={icon} className="w-4 h-4" stroke={iconColor} width={2} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
          {value}
        </p>
      </div>
    </div>
  );
}

function FacilityDetailsModal({ facility, accent, onClose }) {
  return (
    <Modal title={facility.facility_name} subtitle="Facility details" icon={ICONS.facility} onClose={onClose}>
      <div className="space-y-5">
        <div className="rounded-xl overflow-hidden relative">
          <FacilityImage facility={facility} accent={accent} className="h-48" />
          <div className="absolute top-3 right-3">
            <Pill>{facility.facility_status}</Pill>
          </div>
        </div>

        <p className="text-sm text-gray-500">{facility.description || "No description provided."}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FacilityDetailRow
            icon={ICONS.clock}
            iconColor={TOKENS.PRIMARY}
            iconBg={TOKENS.LIGHT}
            label="Weekday hours"
            value={hoursRange(
              facility.weekday_open_time || facility.open_time,
              facility.weekday_close_time || facility.close_time
            )}
          />
          <FacilityDetailRow
            icon={ICONS.clock}
            iconColor={TOKENS.PRIMARY}
            iconBg={TOKENS.LIGHT}
            label="Weekend hours"
            value={hoursRange(
              facility.weekend_open_time || facility.open_time,
              facility.weekend_close_time || facility.close_time
            )}
          />
          <FacilityDetailRow
            icon={ICONS.bookings}
            iconColor="#6d28d9"
            iconBg="#ede9fe"
            label="Slots"
            value={facility.slot_count ?? "—"}
          />
          <FacilityDetailRow
            icon={ICONS.sun}
            iconColor="#c2410c"
            iconBg="#ffedd5"
            label="Without light fee"
            value={`${money(facility.without_light_fee)} · ${facility.without_light_ledger_code || "—"}`}
          />
          <FacilityDetailRow
            icon={ICONS.bulb}
            iconColor="#7c3aed"
            iconBg="#ede9fe"
            label="With light fee"
            value={`${money(facility.with_light_fee)} · ${facility.with_light_ledger_code || "—"}`}
          />
          <FacilityDetailRow
            icon={ICONS.lighting}
            iconColor="#7c3aed"
            iconBg="#ede9fe"
            label="Light required from"
            value={
              facility.light_required_from
                ? `${String(facility.light_required_from).slice(0, 5)} (auto-billed)`
                : "Not set — lighting is manual/optional"
            }
          />
        </div>

        <div className="flex justify-end pt-3 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
// Overall master time-slot window (GET/PUT /api/admin/time-slots) — the
// 30-minute slots every facility's hours are wired from. Widening it adds the
// missing slots and re-syncs every facility server-side; existing slots are
// never removed, so it can't be narrowed from here.
function TimeSlotWindowCard({ onUpdated }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const applyInfo = (data) => {
    setInfo(data);
    setStartTime(data?.day_start || "");
    setEndTime(data?.day_end || "");
  };

  useEffect(() => {
    listTimeSlots()
      .then(applyInfo)
      .catch((err) => setError(err?.response?.data?.message || "Could not load the time slot window."))
      .finally(() => setLoading(false));
  }, []);

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setFieldErrors({});
    if (!startTime || !endTime) {
      setError("Please select both a start time and an end time.");
      return;
    }
    if (endTime <= startTime) {
      setFieldErrors({ end_time: ["End time must be after start time."] });
      return;
    }
    setSaving(true);
    try {
      const result = await updateTimeSlotWindow({ start_time: startTime, end_time: endTime });
      applyInfo(result);
      const synced = result?.facilities_synced ?? 0;
      toast.success(
        `Time slot window updated — ${result?.added_slot_count ?? 0} slot(s) added, ${synced} facilit${
          synced === 1 ? "y" : "ies"
        } synced.`
      );
      onUpdated?.();
    } catch (err) {
      const info = errInfo(err, "Could not update the time slot window.");
      setError(info.message);
      setFieldErrors(info.fields);
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-4 mb-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: TOKENS.LIGHT }}>
          <Icon path={ICONS.clock} className="w-5 h-5" stroke={TOKENS.PRIMARY} width={1.8} />
        </div>
        <div>
          <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
            Overall Time Slot Window
          </p>
          <p className="text-xs text-gray-500">
            {loading
              ? "Loading…"
              : info?.day_start && info?.day_end
              ? `Current: ${info.day_start} – ${info.day_end} · ${info.total_slots ?? 0} slots`
              : "No time slots configured yet."}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner className="w-6 h-6 text-blue-600" />
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label className={labelClass}>Start time</label>
            <input
              type="time"
              className={inputClass}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              step={1800}
              required
            />
            {fieldError("start_time") && <p className="text-xs text-red-600 mt-1">{fieldError("start_time")}</p>}
          </div>
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label className={labelClass}>End time</label>
            <input
              type="time"
              className={inputClass}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              step={1800}
              required
            />
            {fieldError("end_time") && <p className="text-xs text-red-600 mt-1">{fieldError("end_time")}</p>}
          </div>
          <button type="submit" disabled={saving} className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}>
            {saving ? "Updating…" : "Update Window"}
          </button>
        </div>
      )}

      <p className="text-[11px] text-gray-400 mt-2">
        Times must be on :00 or :30. Adds any missing 30-minute slots in this range and re-syncs every facility —
        existing slots are never removed.
      </p>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </form>
  );
}

export default function FacilityManagement() {
  const { can } = useAuth();

  const [facilities, setFacilities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [facilityNames, setFacilityNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // modal: { type: "create" | "edit", facility? }
  const [modal, setModal] = useState(null);
  const [detailFacility, setDetailFacility] = useState(null);

  const canCreate = can("create_facilities");
  const canEdit = can("edit_facilities");
  const canManageTimeSlots = can("manage_time_slots");

  const fetchFacilities = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (statusFilter) params.facility_status_id = statusFilter;
    listFacilities(params)
      .then(setFacilities)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load facilities."))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  // Debounce the search/filter-driven refetch.
  useEffect(() => {
    const t = setTimeout(fetchFacilities, 300);
    return () => clearTimeout(t);
  }, [fetchFacilities]);

  useEffect(() => {
    listFacilityStatuses()
      .then(setStatuses)
      .catch(() => toast.error("Could not load facility statuses."));
    listFacilityNames()
      .then(setFacilityNames)
      .catch(() => toast.error("Could not load facility names."));
  }, []);

  const onSaved = () => {
    setModal(null);
    fetchFacilities();
  };

  // Derived, from whatever's currently loaded (respects the active
  // search/status filter, same as the grid below it).
  const activeCount = facilities.filter((f) => String(f.facility_status).trim().toLowerCase() === "active").length;
  const notBookableCount = facilities.length - activeCount;
  const totalSlots = facilities.reduce((sum, f) => sum + (Number(f.slot_count) || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
            Facilities
          </h1>
          <p className="text-sm text-gray-500">Manage bookable sports facilities and their booking fees.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => setModal({ type: "create" })}
            disabled={statuses.length === 0 || facilityNames.length === 0}
            title={
              statuses.length === 0
                ? "Loading facility statuses…"
                : facilityNames.length === 0
                ? "Loading facility names…"
                : undefined
            }
            className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Add Facility
          </button>
        )}
      </div>

      {canManageTimeSlots && <TimeSlotWindowCard onUpdated={fetchFacilities} />}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <StatCard label="Total Facilities" value={facilities.length} icon={ICONS.facility} accent={TOKENS.PRIMARY} />
        <StatCard label="Active Facilities" value={activeCount} icon={ICONS.check} accent="#15803d" />
        <StatCard label="Total Slots" value={totalSlots} icon={ICONS.bookings} accent="#6d28d9" />
        <StatCard label="Not Bookable" value={notBookableCount} icon={ICONS.block} accent="#b91c1c" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={`${inputClass} w-auto`}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s.category_id} value={s.category_id}>
              {s.description}
            </option>
          ))}
        </select>
      </div>

      {/* Facility cards */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-16 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading facilities…</p>
        </div>
      ) : facilities.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm">
          <EmptyState icon={ICONS.facility} text="No facilities found." />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {facilities.map((f, i) => {
              const accent = facilityAccent(i);
              return (
                <div
                  key={f.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition overflow-hidden flex flex-col"
                >
                  <div className="relative">
                    <FacilityImage facility={f} accent={accent} />
                    <div className="absolute top-3 right-3">
                      <Pill>{f.facility_status}</Pill>
                    </div>
                  </div>

                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-start gap-3 mb-4">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: accent.bg }}
                      >
                        <Icon path={ICONS.facility} className="w-5 h-5" stroke={accent.color} width={1.8} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate" style={{ color: TOKENS.NAVY }}>
                          {f.facility_name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{f.description || "—"}</p>
                      </div>
                    </div>

                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Icon path={ICONS.clock} className="w-3.5 h-3.5 flex-shrink-0" width={2} />
                          <span className="whitespace-nowrap">
                            Wkdy {hoursRange(f.weekday_open_time || f.open_time, f.weekday_close_time || f.close_time)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Icon path={ICONS.clock} className="w-3.5 h-3.5 flex-shrink-0" width={2} />
                          <span className="whitespace-nowrap">
                            Wknd {hoursRange(f.weekend_open_time || f.open_time, f.weekend_close_time || f.close_time)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Icon path={ICONS.bookings} className="w-3.5 h-3.5 flex-shrink-0" width={2} />
                          <span>{f.slot_count ?? "—"} Slots</span>
                        </div>
                      </div>
                      <div className="space-y-1.5 text-right flex-shrink-0">
                        <div className="flex items-center justify-end gap-1 text-xs font-semibold" style={{ color: TOKENS.NAVY }}>
                          <Icon path={ICONS.sun} className="w-3.5 h-3.5 flex-shrink-0" stroke="#c2410c" width={2} />
                          {f.without_light_fee != null ? Number(f.without_light_fee).toFixed(2) : "—"}
                        </div>
                        <div className="flex items-center justify-end gap-1 text-xs font-semibold" style={{ color: TOKENS.NAVY }}>
                          <Icon path={ICONS.bulb} className="w-3.5 h-3.5 flex-shrink-0" stroke="#7c3aed" width={2} />
                          {f.with_light_fee != null ? Number(f.with_light_fee).toFixed(2) : "—"}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => setDetailFacility(f)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-lg text-xs px-3 py-2 transition"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                        View Details
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setModal({ type: "edit", facility: f })}
                          disabled={statuses.length === 0}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ color: TOKENS.PRIMARY }}
                        >
                          <Icon path={ICONS.edit} className="w-4 h-4" width={1.8} />
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-4">
            Showing {facilities.length} facilit{facilities.length === 1 ? "y" : "ies"}.
          </p>
        </>
      )}

      {/* Modals */}
      {detailFacility && (
        <FacilityDetailsModal
          facility={detailFacility}
          accent={facilityAccent(facilities.findIndex((f) => f.id === detailFacility.id))}
          onClose={() => setDetailFacility(null)}
        />
      )}
      {modal?.type === "create" && (
        <Modal
          title="Add Facility"
          subtitle="Create a new bookable sports facility"
          icon={ICONS.facility}
          onClose={() => setModal(null)}
        >
          <FacilityForm
            mode="create"
            statuses={statuses}
            facilityNames={facilityNames}
            onCancel={() => setModal(null)}
            onSaved={onSaved}
          />
        </Modal>
      )}
      {modal?.type === "edit" && (
        <Modal
          title="Edit Facility"
          subtitle="Update facility details and manage slots"
          icon={ICONS.facility}
          maxWidthClass="max-w-4xl"
          onClose={() => setModal(null)}
        >
          <FacilityForm
            mode="edit"
            initial={modal.facility}
            statuses={statuses}
            onCancel={() => setModal(null)}
            onSaved={onSaved}
          />
        </Modal>
      )}
    </div>
  );
}
